/**
 * Create Invitation – Step 1: Add participants (対象者の追加)
 *
 * First screen when an admin creates an invitation. The admin picks who will
 * receive the invitation by moving names from "available" to "selected".
 * On 「保存して次へ進む」 the selected list is passed to Step 2.
 */
import React, { useEffect, useState } from 'react';
import Roadmap from '../Roadmap';
import NameSelector from './NameSelector';
import { Participant } from './types';
import './CreateInvitationStep1.css';
import backButtonIcon from '../../../../../../assests/icons/back-button.png';

export interface CreateInvitationStep1Props {
  /** List of participants available to invite (from API, status = registered) */
  availableParticipants?: Participant[];
  /** Initial selected participants (when returning from Step 2) */
  initialSelected?: Participant[];
  /** Callback when back button is clicked */
  onBack?: () => void;
  /** Callback when save and proceed button is clicked */
  onSaveAndProceed?: (selectedParticipants: Participant[]) => void;
}

const CreateInvitationStep1: React.FC<CreateInvitationStep1Props> = ({
  availableParticipants: availableFromApi = [],
  initialSelected = [],
  onBack,
  onSaveAndProceed,
}) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const emailsSelected = new Set(initialSelected.map((p) => p.email));
  const initialAvailable = availableFromApi.filter((p) => !emailsSelected.has(p.email));

  const [availableParticipants, setAvailableParticipants] = useState<Participant[]>(initialAvailable);
  const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>(initialSelected);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const emailsSelectedSet = new Set(selectedParticipants.map((p) => p.email));
    setAvailableParticipants(availableFromApi.filter((p) => !emailsSelectedSet.has(p.email)));
  }, [availableFromApi]);

  useEffect(() => {
    if (
      initialSelected.length > 0 &&
      selectedParticipants.length === 0 &&
      availableParticipants.length === availableFromApi.length
    ) {
      setSelectedParticipants(initialSelected);
      setAvailableParticipants(availableFromApi.filter((p) => !initialSelected.some((s) => s.email === p.email)));
    }
  }, [initialSelected]);

  // ---------------------------------------------------------------------------
  // Handlers – move participants
  // ---------------------------------------------------------------------------
  const handleMoveRight = (participant: Participant) => {
    setAvailableParticipants((prev) => prev.filter((p) => p.email !== participant.email));
    setSelectedParticipants((prev) => [...prev, participant]);
  };

  // ----------
  const handleMoveLeft = (participant: Participant) => {
    setSelectedParticipants((prev) => prev.filter((p) => p.email !== participant.email));
    setAvailableParticipants((prev) => [...prev, participant]);
  };

  // ----------
  const handleMoveAll = () => {
    setSelectedParticipants((prev) => [...prev, ...availableParticipants]);
    setAvailableParticipants([]);
  };

  // ----------
  const handleSaveAndProceed = () => {
    if (onSaveAndProceed) {
      onSaveAndProceed(selectedParticipants);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="create-invitation-step1">
      <div className="create-invitation-step1__header">
        <button
          className="create-invitation-step1__back-btn"
          onClick={onBack}
          aria-label="戻る"
        >
          <img src={backButtonIcon} alt="" className="create-invitation-step1__back-icon" aria-hidden />
        </button>
        <h1 className="create-invitation-step1__title">招待を作成</h1>
      </div>

      <div className="create-invitation-step1__roadmap">
        <Roadmap currentStep={1} />
      </div>

      <div className="create-invitation-step1__name-selection">
        <NameSelector
          availableParticipants={availableParticipants}
          selectedParticipants={selectedParticipants}
          onMoveRight={handleMoveRight}
          onMoveLeft={handleMoveLeft}
          onMoveAll={handleMoveAll}
        />
      </div>

      <div className="create-invitation-step1__actions">
        <button
          className="create-invitation-step1__save-btn"
          onClick={handleSaveAndProceed}
        >
          保存して次へ進む
        </button>
      </div>
    </div>
  );
};

export default CreateInvitationStep1;
